import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../LanguageContext';
import { 
  Printer, 
  CheckCircle, 
  Clock, 
  AlertCircle, 
  X,
  Loader2,
  FileImage
} from 'lucide-react';
import { Button } from '../ui/button';

export default function PrintStatusPanel({ queue, connectionStatus }) {
  const { t, language } = useLanguage();

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-400" />;
      case 'printing':
        return <Loader2 className="w-4 h-4 text-blue-400 animate-spin" />;
      case 'error':
        return <AlertCircle className="w-4 h-4 text-red-400" />;
      case 'queued':
      default:
        return <Clock className="w-4 h-4 text-yellow-400" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed':
        return language === 'he' ? 'הושלם' : 'Completed';
      case 'printing':
        return language === 'he' ? 'מדפיס...' : 'Printing...';
      case 'error':
        return language === 'he' ? 'שגיאה' : 'Error';
      case 'queued':
      default:
        return language === 'he' ? 'בתור' : 'Queued';
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="fixed bottom-0 left-0 right-0 bg-black/90 backdrop-blur-lg border-t border-white/20 p-4 z-50"
    >
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <Printer className="w-5 h-5 text-white" />
            <h3 className="text-white font-semibold">
              {language === 'he' ? 'תור הדפסה' : 'Print Queue'}
            </h3>
            <span className="bg-violet-600 text-white text-xs px-2 py-1 rounded-full">
              {queue.length}
            </span>
          </div>
          
          <div className="flex items-center gap-2 text-sm text-slate-300">
            <span className={
              connectionStatus === 'connected' ? 'text-green-400' :
              connectionStatus === 'error' ? 'text-red-400' :
              'text-yellow-400'
            }>
              {connectionStatus === 'connected' ? (language === 'he' ? 'מחובר' : 'Connected') :
               connectionStatus === 'error' ? (language === 'he' ? 'שגיאה' : 'Error') :
               (language === 'he' ? 'מתחבר...' : 'Connecting...')}
            </span>
          </div>
        </div>

        <div className="space-y-2 max-h-32 overflow-y-auto">
          <AnimatePresence>
            {queue.map((job, index) => (
              <motion.div
                key={job.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="flex items-center gap-3 bg-white/5 rounded-lg p-3"
              >
                {/* Job Thumbnail */}
                <div className="w-12 h-12 rounded bg-black/50 overflow-hidden flex-shrink-0">
                  <img
                    src={job.image}
                    alt="Print job"
                    className="w-full h-full object-cover"
                  />
                </div>

                {/* Job Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    {getStatusIcon(job.status)}
                    <span className="text-white text-sm font-medium truncate">
                      {language === 'he' ? `משימה #${job.id}` : `Job #${job.id}`}
                    </span>
                  </div>
                  <div className="text-xs text-slate-400">
                    {getStatusText(job.status)} • 
                    {new Date(job.timestamp).toLocaleTimeString()}
                  </div>
                </div>

                {/* Progress Bar for Active Jobs */}
                {job.status === 'printing' && (
                  <div className="w-20">
                    <div className="h-1 bg-white/20 rounded-full overflow-hidden">
                      <motion.div
                        className="h-full bg-blue-500"
                        initial={{ width: '0%' }}
                        animate={{ width: '60%' }}
                        transition={{ duration: 2, repeat: Infinity, repeatType: 'reverse' }}
                      />
                    </div>
                  </div>
                )}

                {/* Cancel Button for Queued Jobs */}
                {job.status === 'queued' && (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </motion.div>
            ))}
          </AnimatePresence>

            {queue.length === 0 && (
              <div className="text-center py-4 text-slate-400 text-sm">
                <Printer className="w-8 h-8 mx-auto mb-2 opacity-50" />
                {language === 'he' ? 'אין משימות הדפסה בתור' : 'No print jobs in queue'}
              </div>
            )}
          </div>
        </div>
      </motion.div>
    );
  }
